import { useState, useEffect, useRef } from "react";
import { Plus, Calendar, Users, LogOut, ArrowRight, Trash2, X, Loader2, ChevronDown, UserX } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getMyEvents, createEvent, deleteEvent, deleteMyAccount, claimMyPendingInvites, getMyPlan, type MyPlan, type PlanTier } from "../lib/api";
import type { Event, EventRole } from "../lib/types";

const PLAN_META: Record<PlanTier, { label: string; cls: string }> = {
  free: { label: "Free", cls: "bg-rc-mist text-rc-charcoal border border-rc-fog" },
  pro: { label: "Pro", cls: "bg-rc-berry/8 text-rc-berry border border-rc-berry/25" },
  unlimited: { label: "Unlimited", cls: "bg-rc-lime/25 text-rc-black border border-rc-lime/40" },
};

interface Props {
  onSelectEvent: (event: Event) => void;
}

type EventStatus = Event["status"];
type EventMode = Event["event_mode"];

const STATUS_META: Record<EventStatus, { label: string; pillCls: string; dot: boolean }> = {
  active: { label: "Live", pillCls: "bg-rc-lime/25 text-rc-black border border-rc-lime/40", dot: true },
  waiting: { label: "Vorbereitung", pillCls: "bg-rc-mist text-rc-slate border border-rc-fog", dot: false },
  completed: { label: "Abgeschlossen", pillCls: "bg-rc-mist text-rc-slate border border-rc-fog", dot: false },
};

const MODE_META: Record<EventMode, { label: string; cls: string }> = {
  facilitated: { label: "Workshop", cls: "bg-rc-cream text-rc-charcoal border border-rc-fog" },
  survey: { label: "Survey", cls: "bg-rc-berry/8 text-rc-berry border border-rc-berry/25" },
};

const ROLE_META: Record<EventRole, { label: string; cls: string } | null> = {
  owner: null,
  co_admin: {
    label: "Co-Admin",
    cls: "bg-rc-berry/8 text-rc-berry border border-rc-berry/25",
  },
  co_mod: {
    label: "Co-Moderator",
    cls: "bg-rc-mist text-rc-charcoal border border-rc-fog",
  },
};

export default function EventsView({ onSelectEvent }: Props) {
  const { user, signOut } = useAuth();
  const [events, setEvents] = useState<Array<Event & { role: EventRole }>>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [plan, setPlan] = useState<MyPlan | null>(null);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);

  const load = async () => {
    setLoading(true);
    await claimMyPendingInvites().catch(() => { /* no-op: best-effort backfill */ });
    const [data, myPlan] = await Promise.all([
      getMyEvents(),
      getMyPlan().catch(() => null),
    ]);
    setEvents(data);
    setPlan(myPlan);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEvent(deleteTarget.id);
      setEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-rc-cream text-rc-black flex flex-col font-sans antialiased">
      <ConsoleHeader
        email={user?.email ?? ""}
        plan={plan}
        onSignOut={signOut}
        onDeleteAccount={() => setShowDeleteAccount(true)}
      />

      <main className="flex-1 max-w-marketing mx-auto w-full px-6 sm:px-8 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10 sm:mb-12">
          <div>
            <h1 className="text-[32px] sm:text-[40px] font-bold text-rc-black tracking-[-0.02em] leading-[1.05]">
              Events.
            </h1>
            <p className="text-rc-charcoal text-[15px] sm:text-[16px] leading-[1.55] mt-3 max-w-[34rem]">
              Lege ein Event an, teile den Join-Code,{" "}
              <strong className="font-semibold text-rc-black">lass die KI den Rest machen</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 text-rc-paper rounded-pill px-5 py-3 text-[14px] font-semibold active:scale-[0.97] transition ease-rc-out self-start sm:self-auto shadow-berry"
          >
            <Plus className="w-4 h-4" />
            Neues Event
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24" role="status" aria-label="Lade Events">
            <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <EmptyState onCreate={() => setShowCreate(true)} />
        ) : (
          <ul className="grid grid-cols-1 gap-3">
            {events.map((event) => (
              <li key={event.id}>
                <EventCard
                  event={event}
                  onOpen={() => onSelectEvent(event)}
                  onDelete={() => setDeleteTarget(event)}
                />
              </li>
            ))}
          </ul>
        )}
      </main>

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreate={(event) => {
            setEvents((prev) => [{ ...event, role: "owner" as const }, ...prev]);
            setShowCreate(false);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteEventModal
          event={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}

      {showDeleteAccount && (
        <DeleteAccountModal
          email={user?.email ?? ""}
          onCancel={() => setShowDeleteAccount(false)}
        />
      )}
    </div>
  );
}

function DeleteAccountModal({ email, onCancel }: { email: string; onCancel: () => void }) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = confirmText.trim().toLowerCase() === email.trim().toLowerCase();

  const handleConfirm = async () => {
    if (!matches) return;
    setError(null);
    setDeleting(true);
    try {
      await deleteMyAccount();
      window.location.href = "/";
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kontolöschung fehlgeschlagen.");
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-rc-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-md bg-rc-paper border border-rc-fog rounded-rc-lg shadow-cardLg p-7 animate-fade-up">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 shrink-0 rounded-full bg-rc-danger/10 border border-rc-danger/30 flex items-center justify-center">
            <UserX className="w-5 h-5 text-rc-danger" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[19px] font-bold text-rc-black tracking-[-0.01em]">Konto löschen</h2>
            <p className="text-rc-charcoal text-[14px] leading-[1.6] mt-2">
              Das löscht dein Konto und <strong className="font-semibold text-rc-black">alle deine Events</strong> mit
              ihren Antworten unwiderruflich. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-5 bg-rc-danger/8 border border-rc-danger/30 rounded-xl px-4 py-3 text-rc-danger text-[14px] font-medium">
            {error}
          </div>
        )}

        <label htmlFor="confirm-email" className="block text-[13px] text-rc-charcoal mt-6 mb-2">
          Gib zur Bestätigung <strong className="font-semibold text-rc-black">{email}</strong> ein:
        </label>
        <input
          id="confirm-email"
          type="email"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoComplete="off"
          className="w-full bg-rc-cream border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-danger focus:ring-2 focus:ring-rc-danger/15 transition-colors"
        />

        <div className="flex gap-3 mt-7">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 inline-flex items-center justify-center rounded-pill border border-rc-fog hover:border-rc-charcoal/30 text-rc-charcoal hover:text-rc-black px-5 py-3 text-[14px] font-semibold transition-colors disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || deleting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-pill bg-rc-danger hover:bg-rc-danger/90 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper px-5 py-3 text-[14px] font-semibold active:scale-[0.98] transition"
          >
            {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
            Endgültig löschen
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsoleHeader({
  email,
  plan,
  onSignOut,
  onDeleteAccount,
}: {
  email: string;
  plan: MyPlan | null;
  onSignOut: () => void;
  onDeleteAccount: () => void;
}) {
  const planMeta = plan ? PLAN_META[plan.plan] : null;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <header
      className="sticky top-0 z-40 border-b border-rc-fog"
      style={{
        background: "rgba(250, 248, 244, 0.86)",
        backdropFilter: "saturate(140%) blur(10px)",
        WebkitBackdropFilter: "saturate(140%) blur(10px)",
      }}
    >
      <div className="max-w-marketing mx-auto px-6 sm:px-8 py-4 flex items-center gap-4">
        <a href="/" aria-label="READYCAL Startseite" className="inline-flex items-center">
          <img src="/brand/readycal-logo.png" alt="READYCAL" className="h-5 w-auto block" />
        </a>
        <span aria-hidden className="text-rc-fog select-none">/</span>
        <span className="text-[13px] font-semibold text-rc-black tracking-[-0.01em]">Events</span>

        <div className="ml-auto flex items-center gap-3">
          {planMeta && (
            <span
              className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-pill ${planMeta.cls}`}
              title={
                plan?.plan === "free"
                  ? "Free: 1 aktives Event, 40 Teilnehmer. Bezahlpläne kommen bald."
                  : `Plan: ${planMeta.label}`
              }
            >
              {planMeta.label}
            </span>
          )}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-charcoal hover:text-rc-black border border-rc-fog hover:border-rc-charcoal/30 rounded-pill pl-3 pr-2 py-1.5 transition-colors ease-rc-out max-w-[15rem]"
            >
              <span className="truncate">{email || "Konto"}</span>
              <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-56 bg-rc-paper border border-rc-fog rounded-rc shadow-cardLg py-1.5 z-50 animate-fade-up"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onSignOut(); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] font-medium text-rc-charcoal hover:text-rc-black hover:bg-rc-cream transition-colors text-left"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
                <div className="h-px bg-rc-fog my-1" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); onDeleteAccount(); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] font-medium text-rc-danger hover:bg-rc-danger/8 transition-colors text-left"
                >
                  <UserX className="w-4 h-4" />
                  Konto löschen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function EventCard({
  event,
  onOpen,
  onDelete,
}: {
  event: Event & { role: EventRole };
  onOpen: () => void;
  onDelete: () => void;
}) {
  const status = STATUS_META[event.status];
  const mode = MODE_META[event.event_mode];
  const roleMeta = ROLE_META[event.role];
  const dateLabel = new Date(event.created_at).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <article className="group bg-rc-paper border border-rc-fog hover:border-rc-charcoal/30 rounded-rc-lg px-6 py-5 transition-colors ease-rc-out">
      <div className="flex items-start gap-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mb-1.5">
            <h3 className="text-rc-black font-semibold text-[16px] tracking-[-0.01em] truncate">
              {event.title || "Ohne Titel"}
            </h3>
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-pill ${status.pillCls}`}
            >
              {status.dot && (
                <span className="relative inline-flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-rc-lime-700 opacity-70 animate-shimmer" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-rc-lime-700" />
                </span>
              )}
              {status.label}
            </span>
            <span
              className={`text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-pill ${mode.cls}`}
            >
              {mode.label}
            </span>
            {roleMeta && (
              <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.14em] rounded-pill px-2.5 py-1 ${roleMeta.cls}`}>
                {roleMeta.label}
              </span>
            )}
          </div>
          {event.description && (
            <p className="text-rc-charcoal text-[14px] leading-[1.5] mb-3 line-clamp-2">{event.description}</p>
          )}
          <div className="flex items-center flex-wrap gap-x-5 gap-y-1.5 text-[12px] text-rc-slate font-medium">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {dateLabel}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono font-semibold tracking-[0.18em] text-rc-berry">
              {event.join_code}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {event.role === "owner" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              aria-label={`Event „${event.title}" löschen`}
              className="p-2 text-rc-slate hover:text-rc-danger hover:bg-rc-danger/8 rounded-full transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-1.5 bg-rc-black hover:bg-rc-graphite text-rc-paper rounded-pill px-4 py-2 text-[13px] font-semibold transition-colors ease-rc-out"
          >
            Öffnen
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="bg-rc-paper border border-rc-fog rounded-rc-lg py-16 px-8 flex flex-col items-center text-center gap-5 shadow-cardLg">
      <div className="w-14 h-14 rounded-full bg-rc-berry/10 border border-rc-berry/25 flex items-center justify-center">
        <Users className="w-6 h-6 text-rc-berry" />
      </div>
      <div>
        <h2 className="text-rc-black font-bold text-[20px] tracking-[-0.01em] mb-2">
          Noch keine Events.
        </h2>
        <p className="text-rc-charcoal text-[14px] max-w-sm leading-[1.55]">
          Lege dein erstes Event an, um loszulegen. Teile den{" "}
          <strong className="font-semibold text-rc-black">Join-Code</strong>{" "}
          mit deinen Teilnehmer:innen.
        </p>
      </div>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 text-rc-paper rounded-pill px-6 py-3 text-[14px] font-semibold active:scale-[0.97] transition ease-rc-out shadow-berry"
      >
        <Plus className="w-4 h-4" />
        Erstes Event anlegen
      </button>
    </div>
  );
}

function DeleteEventModal({
  event,
  deleting,
  onCancel,
  onConfirm,
}: {
  event: Event;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell onClose={onCancel} title="Event löschen" labelledBy="delete-event-title">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-full bg-rc-danger/10 border border-rc-danger/30 flex items-center justify-center flex-shrink-0">
          <Trash2 className="w-5 h-5 text-rc-danger" />
        </div>
        <div>
          <p id="delete-event-title" className="text-rc-black font-semibold text-[17px] tracking-[-0.01em]">
            Event löschen
          </p>
          <p className="text-rc-slate text-[13px] mt-0.5">Kann nicht rückgängig gemacht werden.</p>
        </div>
      </div>
      <p className="text-rc-charcoal text-[14px] leading-[1.6] mb-7">
        Alle Fragen, Antworten, Cluster und Insights für{" "}
        <strong className="font-semibold text-rc-black">„{event.title || "Ohne Titel"}"</strong>{" "}
        werden dauerhaft gelöscht.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={deleting}
          className="flex-1 inline-flex items-center justify-center bg-transparent border-[1.5px] border-rc-fog hover:border-rc-charcoal text-rc-black rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={deleting}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-rc-danger hover:bg-[#b5102f] disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors active:scale-[0.97]"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Löschen"}
        </button>
      </div>
    </ModalShell>
  );
}

function CreateEventModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (event: Event) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<EventMode>("facilitated");
  const [rotationInterval, setRotationInterval] = useState<string>("60");
  const [autoRotate, setAutoRotate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const interval = mode === "survey" && autoRotate ? parseInt(rotationInterval, 10) || null : null;
      const event = await createEvent(title.trim(), description.trim(), mode, interval);
      onCreate(event);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Konnte Event nicht anlegen");
    } finally {
      setCreating(false);
    }
  };

  const inputClass =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors";

  return (
    <ModalShell onClose={onClose} title="Neues Event anlegen" labelledBy="create-event-title">
      <h2 id="create-event-title" className="text-rc-black font-bold text-[20px] tracking-[-0.015em] mb-6">
        Neues Event anlegen.
      </h2>

      <form onSubmit={handleCreate} className="flex flex-col gap-4">
        {error && (
          <div className="bg-rc-danger/8 border border-rc-danger/30 rounded-xl px-4 py-3 text-rc-danger text-[14px] font-medium">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="event-title"
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-charcoal block mb-2"
          >
            Event-Titel
          </label>
          <input
            id="event-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Product at Heart 2026"
            required
            autoFocus
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="event-description"
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-charcoal block mb-2"
          >
            Beschreibung
          </label>
          <textarea
            id="event-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional. Kontext zu diesem Event."
            rows={2}
            className={`${inputClass} resize-none leading-[1.5]`}
          />
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-charcoal block mb-2">
            Modus
          </div>
          <div className="grid grid-cols-2 gap-2">
            {(["facilitated", "survey"] as const).map((m) => {
              const selected = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  aria-pressed={selected}
                  className={`text-left px-4 py-3 rounded-xl text-[14px] font-semibold border-[1.5px] transition-colors ease-rc-out ${
                    selected
                      ? "bg-rc-berry/8 border-rc-berry text-rc-berry"
                      : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black hover:border-rc-charcoal/40"
                  }`}
                >
                  {m === "facilitated" ? "Workshop" : "Survey"}
                </button>
              );
            })}
          </div>
          <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
            {mode === "facilitated"
              ? "Du steuerst, welche Frage offen ist und wann die KI-Analyse läuft."
              : "Alle Fragen sind gleichzeitig offen. KI läuft automatisch. Teilnehmer:innen scannen und antworten selbst."}
          </p>
        </div>

        {mode === "survey" && (
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                role="switch"
                aria-checked={autoRotate}
                onClick={() => setAutoRotate((v) => !v)}
                className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                  autoRotate ? "bg-rc-berry" : "bg-rc-fog"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-rc-paper shadow transition-transform ${
                    autoRotate ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-[14px] text-rc-black font-medium">Fragen automatisch wechseln</span>
            </label>
            {autoRotate && (
              <div className="flex items-center gap-3 mt-3 pl-[3.25rem]">
                <input
                  type="number"
                  value={rotationInterval}
                  onChange={(e) => setRotationInterval(e.target.value)}
                  min="10"
                  max="3600"
                  className="w-24 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black text-center font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
                />
                <span className="text-rc-charcoal text-[13px]">Sekunden pro Frage</span>
              </div>
            )}
          </div>
        )}

        <p className="text-[12px] text-rc-slate leading-[1.5]">
          Ein eindeutiger Join-Code wird automatisch generiert.
        </p>

        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center bg-transparent border-[1.5px] border-rc-fog hover:border-rc-charcoal text-rc-black rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={creating || !title.trim()}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors active:scale-[0.97]"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Event anlegen"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({
  onClose,
  title,
  labelledBy,
  children,
}: {
  onClose: () => void;
  title: string;
  labelledBy: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className="fixed inset-0 z-50 bg-rc-black/40 backdrop-blur-sm flex items-center justify-center px-4 py-8 animate-fade-up"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-rc-paper border border-rc-fog rounded-rc-lg p-7 sm:p-8 max-w-md w-full shadow-cardLg">
        <button
          type="button"
          onClick={onClose}
          aria-label={`${title} schließen`}
          className="absolute top-4 right-4 p-1.5 text-rc-slate hover:text-rc-black hover:bg-rc-mist rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );
}
